import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeString } from '../utils/security';
import {
  ORDER_CREATION_PERMISSION_OPTIONS,
  ORDER_CREATION_PERMISSION_DEFAULT,
} from '../constants/orderCreationPermission';
import OrderCreationPermissionBadge from '../components/OrderCreationPermissionBadge';
import {
  fetchTpTechnicians,
  createTpTechnician,
  updateTpTechnician,
} from '../services/tpTechniciansService';
import { useConfirmDialog } from '../contexts/ConfirmContext';

const PHONE_REGEX = /^(\+90|0)?[0-9]{10}$/;

const emptyForm = {
  kullaniciAdi: '',
  sifre: '',
  ad: '',
  soyad: '',
  telefon: '',
  orderCreationPermission: ORDER_CREATION_PERMISSION_DEFAULT,
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const hatalar = error?.data?.hatalar;
  if (Array.isArray(hatalar) && hatalar.length > 0) {
    const messages = hatalar.map((h) => h.mesaj || h.message).filter(Boolean);
    if (messages.length) return messages.join('. ');
  }
  return error?.message || error?.data?.mesaj || fallback;
};

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const TechServiceTechnicianManagementPage = () => {
  const { user } = useAuth();
  const { confirm } = useConfirmDialog();

  const techServiceId = useMemo(() => {
    const ts = user?.techService;
    if (!ts) return '';
    if (typeof ts === 'object') return String(ts._id || ts.id || '');
    return String(ts);
  }, [user?.techService]);

  const [activeTab, setActiveTab] = useState('active');
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [modalMode, setModalMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [formState, setFormState] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [formSubmitting, setFormSubmitting] = useState(false);

  const loadTechnicians = useCallback(async () => {
    if (!techServiceId) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetchTpTechnicians(techServiceId, { aktif: 'all' });
      setTechnicians(res?.veri?.teknisyenler || []);
    } catch (e) {
      setError(getErrorMessage(e, 'Teknisyenler yüklenemedi'));
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  }, [techServiceId]);

  useEffect(() => {
    if (user?.rol === 'tech-service' && techServiceId) {
      loadTechnicians();
    }
  }, [user?.rol, techServiceId, loadTechnicians]);

  if (user?.rol === 'tech-person') {
    return <Navigate to="/teknisyen" replace />;
  }
  if (user?.rol !== 'tech-service') {
    return <Navigate to="/" replace />;
  }

  const filteredList = technicians.filter((t) =>
    activeTab === 'active' ? t.aktif !== false : t.aktif === false
  );

  const openCreate = () => {
    setModalMode('create');
    setEditingId(null);
    setFormState(emptyForm);
    setFormError('');
  };

  const openEdit = (row) => {
    setModalMode('edit');
    setEditingId(row.id || row._id);
    setFormState({
      kullaniciAdi: row.kullaniciAdi || '',
      sifre: '',
      ad: row.ad || '',
      soyad: row.soyad || '',
      telefon: row.telefon || '',
      orderCreationPermission: row.orderCreationPermission || ORDER_CREATION_PERMISSION_DEFAULT,
    });
    setFormError('');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingId(null);
    setFormState(emptyForm);
    setFormError('');
  };

  const validateForm = () => {
    const username = formState.kullaniciAdi?.trim() || '';
    if (username.length < 3 || username.length > 50) {
      throw new Error('Kullanıcı adı 3-50 karakter arasında olmalıdır');
    }
    if (!/^[a-zA-Z0-9_]+$/.test(username)) {
      throw new Error('Kullanıcı adı sadece harf, rakam ve alt çizgi içerebilir');
    }
    if (modalMode === 'create') {
      const password = formState.sifre?.trim() || '';
      if (password.length < 6) throw new Error('Şifre en az 6 karakter olmalıdır');
      if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        throw new Error('Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir');
      }
    } else if (formState.sifre?.trim()) {
      const password = formState.sifre.trim();
      if (password.length < 6) throw new Error('Şifre en az 6 karakter olmalıdır');
      if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
        throw new Error('Şifre en az bir küçük harf, bir büyük harf ve bir rakam içermelidir');
      }
    }
    if (formState.telefon?.trim() && !PHONE_REGEX.test(formState.telefon.trim().replace(/\s/g, ''))) {
      throw new Error('Geçerli bir telefon numarası giriniz (örn: 5551234567)');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError('');
    try {
      validateForm();
      const payload = {
        kullaniciAdi: formState.kullaniciAdi.trim(),
        ad: formState.ad,
        soyad: formState.soyad,
        telefon: formState.telefon,
        orderCreationPermission: formState.orderCreationPermission,
      };
      if (formState.sifre?.trim()) payload.sifre = formState.sifre.trim();

      if (modalMode === 'create') {
        await createTpTechnician(techServiceId, payload);
        if (window.showNotification) window.showNotification('success', 'Teknisyen oluşturuldu');
      } else {
        await updateTpTechnician(techServiceId, editingId, payload);
        if (window.showNotification) window.showNotification('success', 'Teknisyen güncellendi');
      }
      closeModal();
      await loadTechnicians();
    } catch (err) {
      setFormError(getErrorMessage(err, 'İşlem başarısız'));
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleToggleActive = async (row) => {
    const id = row.id || row._id;
    const isActive = row.aktif !== false;
    const ok = await confirm({
      title: isActive ? 'Kullanıcıyı pasifleştir' : 'Kullanıcıyı aktifleştir',
      message: isActive
        ? `${row.kullaniciAdi} hesabını pasif yapmak istediğinize emin misiniz?`
        : `${row.kullaniciAdi} hesabını tekrar aktif yapmak istediğinize emin misiniz?`,
      variant: isActive ? 'warning' : 'default',
      confirmLabel: isActive ? 'Pasifleştir' : 'Aktifleştir',
    });
    if (!ok) return;
    try {
      await updateTpTechnician(techServiceId, id, { aktif: !isActive });
      if (window.showNotification) {
        window.showNotification(
          'success',
          isActive ? 'Kullanıcı pasifleştirildi' : 'Kullanıcı aktifleştirildi'
        );
      }
      await loadTechnicians();
    } catch (err) {
      if (window.showNotification) {
        window.showNotification('error', getErrorMessage(err, 'İşlem başarısız'));
      }
    }
  };

  const editingCreatedAt = modalMode === 'edit'
    ? technicians.find((t) => (t.id || t._id) === editingId)?.olusturulma
    : null;

  return (
    <main className="main-content">
      <div className="content-header">
        <div>
          <h1>Kullanıcı Yönetimi</h1>
        </div>
        <button type="button" className="primary-btn" onClick={openCreate}>
          <i className="fas fa-user-plus" aria-hidden />
          <span>Teknisyen Ekle</span>
        </button>
      </div>

      <div className="tabs" style={{ marginBottom: '16px' }}>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Aktif Kullanıcılar
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'passive' ? 'active' : ''}`}
          onClick={() => setActiveTab('passive')}
        >
          Pasif Kullanıcılar
        </button>
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner" />
            <div className="loading-text">Yükleniyor</div>
          </div>
        ) : error ? (
          <div className="error-state" style={{ margin: '16px' }}>
            {sanitizeString(error)}
            <button type="button" className="text-link" onClick={loadTechnicians} style={{ marginLeft: '12px' }}>
              Tekrar dene
            </button>
          </div>
        ) : filteredList.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px', textAlign: 'center' }}>
            <i className="fas fa-users" style={{ fontSize: '32px', color: 'var(--text-light)', marginBottom: '12px' }} />
            <div style={{ color: 'var(--text-gray)', fontSize: '14px' }}>
              {activeTab === 'active' ? 'Aktif teknisyen bulunamadı.' : 'Pasif teknisyen bulunamadı.'}
            </div>
          </div>
        ) : (
          <div className="table-container">
            <table className="admins-table">
              <thead>
                <tr>
                  <th>Kullanıcı adı</th>
                  <th>Ad Soyad</th>
                  <th>Telefon</th>
                  <th>Sipariş yetkisi</th>
                  <th>Oluşturulma</th>
                  <th aria-label="İşlemler" />
                </tr>
              </thead>
              <tbody>
                {filteredList.map((row) => (
                  <tr key={row.id || row._id}>
                    <td className="name-cell">
                      <button
                        type="button"
                        className="tech-service-name-link"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit' }}
                        onClick={() => openEdit(row)}
                      >
                        {sanitizeString(row.kullaniciAdi)}
                      </button>
                    </td>
                    <td>{sanitizeString([row.ad, row.soyad].filter(Boolean).join(' ') || '—')}</td>
                    <td>{sanitizeString(row.telefon || '—')}</td>
                    <td>
                      <OrderCreationPermissionBadge value={row.orderCreationPermission} />
                    </td>
                    <td>{formatDateTime(row.olusturulma || row.createdAt)}</td>
                    <td className="table-actions">
                      <button
                        type="button"
                        className="category-action-btn edit-btn"
                        onClick={() => openEdit(row)}
                        title="Düzenle"
                        aria-label="Düzenle"
                      >
                        <i className="fas fa-edit" />
                      </button>
                      <button
                        type="button"
                        className={`category-action-btn ${row.aktif !== false ? 'delete-btn' : 'edit-btn'}`}
                        onClick={() => handleToggleActive(row)}
                        title={row.aktif !== false ? 'Pasifleştir' : 'Aktifleştir'}
                        aria-label={row.aktif !== false ? 'Pasifleştir' : 'Aktifleştir'}
                      >
                        <i className={row.aktif !== false ? 'fas fa-user-slash' : 'fas fa-user-check'} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalMode && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeModal}>
          <div className="modal" onClick={(ev) => ev.stopPropagation()}>
            <div className="modal-header">
              <h2>{modalMode === 'create' ? 'Teknisyen Ekle' : 'Teknisyen Düzenle'}</h2>
              <button type="button" className="close-btn" onClick={closeModal} aria-label="Kapat">
                <i className="fas fa-times" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="modal-body">
              {formError && (
                <div className="error-state" style={{ marginBottom: '12px' }}>
                  {sanitizeString(formError)}
                </div>
              )}
              <div className="form-grid">
                <label className="form-field">
                  <span>Kullanıcı adı *</span>
                  <input
                    type="text"
                    value={formState.kullaniciAdi}
                    onChange={(ev) => setFormState((p) => ({ ...p, kullaniciAdi: ev.target.value }))}
                    maxLength={50}
                    minLength={3}
                    pattern="[a-zA-Z0-9_]+"
                    required
                  />
                </label>
                <label className="form-field">
                  <span>Şifre {modalMode === 'create' ? '*' : ''}</span>
                  <input
                    type="password"
                    value={formState.sifre}
                    onChange={(ev) => setFormState((p) => ({ ...p, sifre: ev.target.value }))}
                    placeholder={modalMode === 'edit' ? 'Boş bırakırsanız değişmez' : ''}
                    required={modalMode === 'create'}
                  />
                </label>
                <label className="form-field">
                  <span>Ad</span>
                  <input
                    type="text"
                    value={formState.ad}
                    onChange={(ev) => setFormState((p) => ({ ...p, ad: ev.target.value }))}
                    maxLength={50}
                  />
                </label>
                <label className="form-field">
                  <span>Soyad</span>
                  <input
                    type="text"
                    value={formState.soyad}
                    onChange={(ev) => setFormState((p) => ({ ...p, soyad: ev.target.value }))}
                    maxLength={50}
                  />
                </label>
                <label className="form-field">
                  <span>Telefon</span>
                  <input
                    type="tel"
                    value={formState.telefon}
                    onChange={(ev) => setFormState((p) => ({ ...p, telefon: ev.target.value }))}
                    placeholder="5551234567"
                  />
                </label>
                <label className="form-field full-width">
                  <span>Sipariş Oluşturma Yetkisi</span>
                  <select
                    value={formState.orderCreationPermission}
                    onChange={(ev) =>
                      setFormState((p) => ({ ...p, orderCreationPermission: ev.target.value }))
                    }
                  >
                    {ORDER_CREATION_PERMISSION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                {modalMode === 'edit' && (
                  <p
                    className="form-field full-width"
                    style={{ fontSize: '13px', color: 'var(--text-gray)', margin: 0 }}
                  >
                    Oluşturulma: {formatDateTime(editingCreatedAt)}
                  </p>
                )}
              </div>
              <div
                className="modal-footer"
                style={{ marginTop: '20px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
              >
                <button type="button" className="secondary-btn" onClick={closeModal} disabled={formSubmitting}>
                  İptal
                </button>
                <button type="submit" className="primary-btn" disabled={formSubmitting}>
                  {formSubmitting ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default TechServiceTechnicianManagementPage;
